import { useCallback, useEffect, useMemo, useState } from "react"
import { Typography, Stack, IconButton, CircularProgress, Skeleton } from "@mui/material"
import { Cached } from "@mui/icons-material"
import { fetchData } from "../../utils"

function formatTimeAgo(date, now) {
    if (!date) return ''
    const seconds = Math.floor((now - date) / 1000)
    if (seconds < 60) return 'just now'
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`
    return date.toLocaleTimeString()
}

function RefreshData({ setData, setLoading, isMobile }) {
    const [lastFetched, setLastFetched] = useState(null)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const [now, setNow] = useState(() => Date.now())

    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true)
        setLoading(true)
        try {
            const responseData = await fetchData()
            setData(responseData)
            setLastFetched(new Date())
        } finally {
            setIsRefreshing(false)
            setLoading(false)
        }
    }, [setData, setLoading])

    // Initial fetch
    useEffect(() => {
        handleRefresh()
    }, [handleRefresh])

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now())
        }, 10000)
        return () => clearInterval(interval)
    }, [])

    const timeAgo = useMemo(() => (
        formatTimeAgo(lastFetched, now)
    ), [lastFetched, now])

    if (!lastFetched && isRefreshing) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: isMobile ? 0 : 1 }}>
                <Skeleton variant="text" width={120} height={20} />
                {!isMobile && <Skeleton variant="circular" width={24} height={24} />}
            </Stack>
        )
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: isMobile ? 0 : 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {lastFetched ? `last updated ${timeAgo}` : ''}
            </Typography>
            {!isMobile && (
                <IconButton
                    onClick={handleRefresh}
                    size="small"
                    disabled={isRefreshing}
                    sx={{ color: 'text.secondary' }}
                >
                    {isRefreshing ? (
                        <CircularProgress size={16} color="inherit" />
                    ) : (
                        <Cached fontSize="small" />
                    )}
                </IconButton>
            )}
        </Stack>
    )
}

export default RefreshData
